import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Mail, ArrowLeft, Trash2 } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";

const steps = [
  {
    title: "Open the Rallie app",
    desc: "Launch Rallie on your iOS or Android device and sign in to the account you want to delete.",
  },
  {
    title: "Go to Profile",
    desc: "Tap the profile icon in the top-right corner of the Home screen.",
  },
  {
    title: "Open Settings",
    desc: "Tap the gear/settings icon on your profile page.",
  },
  {
    title: "Tap 'Delete Account'",
    desc: "Scroll to the bottom of Settings and tap 'Delete Account'. Confirm the action when prompted.",
  },
];

const dataDeleted = [
  "Your Rallie profile (name, email, avatar)",
  "Your career stats, rankings and match history",
  "Tournaments you created and the players you added",
  "All authentication data associated with your account",
];

const dataRetained = [
  "Anonymised match records that are part of a shared tournament (your name is removed, but the tournament remains intact for other participants)",
  "Aggregated, non-identifiable analytics used for product improvement",
  "Records required for legal or tax compliance (up to the retention period required by law)",
];

export default function DeleteAccount() {
  return (
    <div className="bg-black text-white min-h-screen">
      <Nav />

      <main
        data-testid="delete-account-page"
        className="pt-32 pb-20 max-w-3xl mx-auto px-6 md:px-10"
      >
        <Link
          to="/"
          data-testid="delete-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400 hover:text-[#D4F34A] transition mb-10"
        >
          <ArrowLeft size={14} />
          Back to Rallie
        </Link>

        <div className="text-xs uppercase tracking-widest text-[#D4F34A] mb-3">
          Account & data
        </div>
        <h1
          data-testid="delete-account-heading"
          className="font-display uppercase tracking-tight text-5xl md:text-7xl leading-[0.9]"
        >
          Delete your
          <br />
          Rallie account.
        </h1>
        <p className="mt-6 text-neutral-300 text-base md:text-lg leading-relaxed max-w-2xl">
          You can permanently delete your Rallie account and the personal data
          associated with it at any time — either from within the app or by
          contacting our support team.
        </p>

        {/* Warning */}
        <div
          data-testid="delete-warning"
          className="mt-10 border border-[#D4F34A]/40 bg-[#D4F34A]/[0.05] p-6 flex gap-4"
        >
          <AlertTriangle
            size={22}
            className="text-[#D4F34A] mt-0.5 flex-none"
          />
          <div>
            <div className="font-display uppercase text-sm mb-1">
              This is permanent
            </div>
            <p className="text-sm text-neutral-300 leading-relaxed">
              Deleting your account is irreversible. Your profile, career stats
              and tournament history will be removed. Please export any data
              you want to keep from Profile → Settings → Export before you
              continue.
            </p>
          </div>
        </div>

        {/* Steps */}
        <section className="mt-14">
          <div className="text-xs uppercase tracking-widest text-[#D4F34A] mb-3">
            Option 1 · In the app
          </div>
          <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight leading-tight mb-8">
            Delete from the Rallie app
          </h2>

          <ol className="space-y-4" data-testid="delete-steps">
            {steps.map((s, i) => (
              <li
                key={s.title}
                data-testid={`delete-step-${i + 1}`}
                className="flex gap-4 border border-neutral-900 p-5 bg-neutral-950"
              >
                <div className="font-display text-2xl text-[#D4F34A] w-8 flex-none">
                  0{i + 1}
                </div>
                <div>
                  <div className="font-display uppercase text-base md:text-lg tracking-tight">
                    {s.title}
                  </div>
                  <p className="text-sm text-neutral-400 mt-1 leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* Email option */}
        <section className="mt-16">
          <div className="text-xs uppercase tracking-widest text-[#D4F34A] mb-3">
            Option 2 · By email
          </div>
          <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight leading-tight mb-6">
            Request deletion by email
          </h2>
          <p className="text-neutral-300 text-sm md:text-base leading-relaxed max-w-2xl">
            If you can&apos;t access the app, email us from the address linked to
            your Rallie account. Include your registered name so we can locate
            your profile. We&apos;ll confirm the deletion within{" "}
            <span className="text-white font-semibold">7 days</span> of your
            request.
          </p>

          <a
            href="mailto:privacy@rallie.app?subject=Delete%20my%20Rallie%20account&body=Please%20delete%20my%20Rallie%20account.%0A%0ARegistered%20name%3A%0ARegistered%20email%3A%0A"
            data-testid="delete-email-cta"
            className="mt-6 inline-flex items-center gap-3 bg-[#D4F34A] text-black px-5 py-3 rounded-full font-display uppercase text-sm tracking-widest hover:bg-white transition active:scale-95"
          >
            <Mail size={18} />
            Email privacy@rallie.app
          </a>

          <div className="mt-4 text-[11px] uppercase tracking-widest text-neutral-500">
            Response time · within 7 days
          </div>
        </section>

        {/* Data deleted / retained */}
        <section className="mt-16 grid md:grid-cols-2 gap-4">
          <div
            data-testid="delete-data-deleted"
            className="border border-neutral-900 p-6 bg-neutral-950"
          >
            <div className="flex items-center gap-2 mb-4">
              <Trash2 size={16} className="text-[#D4F34A]" />
              <div className="text-xs uppercase tracking-widest text-[#D4F34A]">
                What we delete
              </div>
            </div>
            <ul className="space-y-2 text-sm text-neutral-300">
              {dataDeleted.map((d) => (
                <li key={d} className="flex gap-2">
                  <span className="text-[#D4F34A]">·</span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            data-testid="delete-data-retained"
            className="border border-neutral-900 p-6 bg-black"
          >
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">
              What may be retained
            </div>
            <ul className="space-y-2 text-sm text-neutral-400">
              {dataRetained.map((d) => (
                <li key={d} className="flex gap-2">
                  <span className="text-neutral-600">·</span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Timeline */}
        <section className="mt-16 border-t border-neutral-900 pt-10">
          <div className="text-xs uppercase tracking-widest text-[#D4F34A] mb-3">
            Timeline
          </div>
          <h3 className="font-display uppercase text-2xl md:text-3xl tracking-tight leading-tight mb-4">
            When your data is removed
          </h3>
          <p className="text-sm md:text-base text-neutral-300 leading-relaxed max-w-2xl">
            Once you confirm deletion in the app — or once we process your
            email request — your personal data is deleted from our production
            systems <span className="text-white">immediately</span>. Encrypted
            backups are automatically purged within{" "}
            <span className="text-white">30 days</span>.
          </p>

          <p className="mt-4 text-sm text-neutral-400 leading-relaxed max-w-2xl">
            Questions about your data? Read our{" "}
            <a
              href="/privacy"
              data-testid="delete-privacy-link"
              className="text-[#D4F34A] hover:underline"
            >
              Privacy Policy
            </a>{" "}
            or email{" "}
            <a
              href="mailto:privacy@rallie.app"
              className="text-[#D4F34A] hover:underline"
            >
              privacy@rallie.app
            </a>
            .
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
}
